<script setup lang="ts">
import { ChevronLeftIcon, MagnifyingGlassPlusIcon } from "@heroicons/vue/24/outline";
import { format as formatDate } from "date-fns/format";
import { useRouter } from "vue-router";

import type { components } from "@/types/openapi.interfaces";

import EmptyReceiptCheckIcon from "@/components/icons/EmptyReceiptCheckIcon.vue";
import EmptyReceiptCrossIcon from "@/components/icons/EmptyReceiptCrossIcon.vue";
import EmptyReceiptPendingIcon from "@/components/icons/EmptyReceiptPendingIcon.vue";
import Dialog from "@/components/ui/Dialog.vue";
import RoundButton from "@/components/ui/RoundButton.vue";

defineProps<{
  previewImageSrc?: string | null;
  fullImageSrc?: string | null;
  created: string;
  status: components["schemas"]["StatusEnum"];
}>();

const router = useRouter();

function goBack() {
  // if no history, go to home
  if (window.history.length <= 1) {
    router.push({ name: "receipts" });
    return;
  }
  router.back();
}
</script>

<template>
  <div class="w-full bg-[#D4DBEC]" role="banner">
    <div class="relative mx-auto h-64 w-full max-w-2xl lg:h-[468px]">
      <div
        v-if="!previewImageSrc"
        class="flex h-full w-full flex-col items-center justify-center gap-6 bg-white py-4"
      >
        <template v-if="status === 'declared'">
          <EmptyReceiptPendingIcon class="h-auto w-52" />

          <p class="text-secondary text-base font-bold lg:text-lg">
            {{ $t("receipt.pending") }}
          </p>
        </template>

        <template
          v-if="
            status === 'filtered_analyzed' ||
            status === 'completed' ||
            status === 'user_reviewed' ||
            status === 'rejected'
          "
        >
          <EmptyReceiptCheckIcon
            v-if="
              status === 'filtered_analyzed' || status === 'completed' || status === 'user_reviewed'
            "
            class="h-auto w-52"
          />

          <EmptyReceiptCrossIcon v-else-if="status === 'rejected'" class="h-auto w-52" />

          <p
            class="text-secondary mx-auto flex max-w-2xs items-center justify-center gap-1.5 text-center text-base lg:text-lg"
          >
            {{ $t("receipt.zoomInstruction") }}
          </p>
        </template>
      </div>

      <img
        v-if="previewImageSrc"
        :src="previewImageSrc"
        :alt="`Votre ticket du ${formatDate(created, 'dd.MM.yy')}`"
        class="pointer-events-none mx-auto h-full w-full object-cover"
      />

      <RoundButton
        class="absolute top-4 left-4 lg:-left-20"
        aria-label="Retour à la page précédente"
        @click="goBack"
      >
        <ChevronLeftIcon class="size-7" aria-hidden="true" />
      </RoundButton>

      <Dialog>
        <template #trigger>
          <RoundButton
            v-if="fullImageSrc"
            class="absolute top-4 right-4 lg:-right-20"
            aria-label="Zoomer sur le ticket"
          >
            <MagnifyingGlassPlusIcon class="size-7" aria-hidden="true" />
          </RoundButton>
        </template>

        <template #content>
          <img
            v-if="fullImageSrc"
            :src="fullImageSrc"
            alt=""
            class="h-full w-auto object-contain"
          />
        </template>
      </Dialog>
    </div>
  </div>
</template>
